/* Copyright (c) 2026 PearlCrm Pty Ltd. All rights reserved. */

/**
 * The persistent softphone.
 *
 * Fixed bottom-right, mounted beside <Routes> so a call survives navigation.
 * Deliberately NOT wrapped in scaledVh/scaledVw: appScale.js applies
 * `body { zoom: 0.8 }`, and a fixed element already resolves against the
 * scaled containing block, so the helpers would scale it twice.
 *
 * When the Call Console is mounted it registers as the inline host and this
 * collapses to a pill -- one engine, one visible set of controls, never two
 * competing call UIs.
 */

import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  Alert,
  Card,
  Collapse,
  Divider,
  IconButton,
  InputAdornment,
  Menu,
  MenuItem,
  Snackbar,
  TextField,
} from "@mui/material";
import {
  Backspace,
  Call,
  CallEnd,
  Dialpad,
  ExpandLess,
  ExpandMore,
  Mic,
  MicOff,
  PhoneInTalk,
} from "@mui/icons-material";
import MDBox from "components/MDBox";
import MDButton from "components/MDButton";
import MDTypography from "components/MDTypography";
import MonoText from "components/MonoText";
import { useTelephony } from "telephony/TelephonyContext";
import {
  AGENT_PRESENCE,
  CALL_STATE,
  CALL_STATE_META,
  PRESENCE_META,
  formatDuration,
} from "telephony/callStates";
// One copy, shared with the console. The dock and the console are two skins
// over one engine, so a control that behaves differently between them is a
// bug by definition.
import { Dialpad as DialpadKeys, DTMF_KEYS, PresenceDot, RoundButton } from "telephony/components";
import { isDialable, sanitizeDialInput } from "telephony/dialNumber";
import { callerIdLabel, distinctFriendlyName } from "telephony/callerIdLabel";
import colors from "styles/tokens/colors";

const cc = colors.callCenter;

const DARK = cc.dark;
const SURFACE = cc.surface;
const MUTED_TEXT = cc.muted;


export default function SoftphoneDock() {
  const navigate = useNavigate();
  const {
    enabled,
    settingsChecked,
    isLeader,
    registered,
    deviceError,
    clearError,
    micError,
    dismissMicError,
    presence,
    changePresence,
    callState,
    activeCall,
    durationSec,
    muted,
    acceptCall,
    rejectCall,
    hangUp,
    toggleMute,
    sendDigits,
    placeCall,
    skipWrapUp,
    inlineHostActive,
    callerIds,
    fromNumber,
    setFromNumberId,
  } = useTelephony();

  const [collapsed, setCollapsed] = useState(true);
  const [presenceAnchor, setPresenceAnchor] = useState(null);
  const [keypadOpen, setKeypadOpen] = useState(false);
  const [dialerOpen, setDialerOpen] = useState(false);
  const [dialNumber, setDialNumber] = useState("");
  const [dialBusy, setDialBusy] = useState(false);
  const [dialError, setDialError] = useState("");

  // Nothing to show until we know telephony exists for this tenant.
  if (!settingsChecked || !enabled) return null;

  const meta = CALL_STATE_META[callState] || CALL_STATE_META[CALL_STATE.IDLE];
  const presenceMeta = PRESENCE_META[presence] || PRESENCE_META.offline;
  const isRinging = callState === CALL_STATE.RINGING;
  const isLive = callState === CALL_STATE.CONNECTED || callState === CALL_STATE.ON_HOLD;
  const isWrapUp = callState === CALL_STATE.WRAP_UP;
  const hasCall = callState !== CALL_STATE.IDLE;

  // The Call Console owns the controls while it is mounted.
  const asPill = inlineHostActive && !isRinging;
  const expanded = !collapsed || isRinging;

  const counterparty =
    activeCall?.direction === "outbound" ? activeCall?.to_address : activeCall?.from_address;
  // Our side of the call: the caller ID on an outbound call, the number the
  // customer dialled on an inbound one.
  const ourLine =
    activeCall?.direction === "outbound" ? activeCall?.from_address : activeCall?.to_address;

  // The same preconditions CallButton enforces on record pages. An agent who
  // is Offline has no registered Device to originate from, so this is not
  // merely a permission check -- the call would fail at Twilio.
  const isAvailable = presence === "available";
  const blockedReason = !isLeader
    ? "The softphone is active in another browser tab."
    : !registered
    ? "Your softphone is still connecting."
    : !isAvailable
    ? `You are ${(presenceMeta.label || "offline").toLowerCase()}. Go Available to place calls.`
    : "";
  // Two digits is not a phone number anywhere, and the pad makes a stray tap
  // easy; this is only meant to catch that, not to validate dialling plans.
  const canDial = !blockedReason && !dialBusy && isDialable(dialNumber);
  const dialHint = dialError || blockedReason;

  const dial = async () => {
    if (!canDial) return;
    setDialBusy(true);
    setDialError("");
    try {
      // No record context: this is the case where the agent has a number and
      // no matching record. The call still lands on its own conversation, and
      // wrap-up is where it gets attached to something.
      await placeCall({ to: dialNumber });
      setDialNumber("");
      setDialerOpen(false);
    } catch (err) {
      setDialError(err?.message || "The call could not be placed.");
    } finally {
      setDialBusy(false);
    }
  };

  return (
    <>
      <Card
        sx={{
          position: "fixed",
          right: 24,
          bottom: 24,
          width: asPill ? 220 : 300,
          zIndex: (theme) => theme.zIndex.drawer - 1,
          borderRadius: "12px",
          border: "1px solid #E8EDF5",
          // The one place a shadow is right: a floating element without one
          // reads as a rendering bug rather than a panel.
          boxShadow: "0 8px 24px rgba(14,35,72,0.14)",
          overflow: "hidden",
        }}
      >
        <MDBox
          onClick={() => !isRinging && setCollapsed((c) => !c)}
          sx={{
            bgcolor: isRinging ? "#1565C8" : DARK,
            px: 1.5,
            height: 40,
            display: "flex",
            alignItems: "center",
            gap: 1,
            cursor: isRinging ? "default" : "pointer",
            // A slow pulse reads as urgent without bouncing the whole panel.
            animation: isRinging ? "pearlRing 1.6s ease-in-out infinite" : "none",
            "@keyframes pearlRing": {
              "0%,100%": { opacity: 1 },
              "50%": { opacity: 0.72 },
            },
          }}
        >
          <PhoneInTalk sx={{ color: "#fff", fontSize: 18 }} />
          <MDTypography variant="button" color="white" sx={{ flexGrow: 1 }}>
            {hasCall ? meta.label : "Softphone"}
          </MDTypography>
          {isLive && (
            <MonoText variant="button" sx={{ color: "#fff" }}>
              {formatDuration(durationSec)}
            </MonoText>
          )}
          {!isRinging &&
            (expanded ? (
              <ExpandMore sx={{ color: "#fff", fontSize: 18 }} />
            ) : (
              <ExpandLess sx={{ color: "#fff", fontSize: 18 }} />
            ))}
        </MDBox>

        <Collapse in={expanded}>
          <MDBox p={2}>
            {!isLeader && (
              <Alert severity="info" sx={{ mb: 1.5, py: 0.25 }}>
                Softphone active in another tab.
              </Alert>
            )}
            {isLeader && !registered && !deviceError && (
              <Alert severity="info" sx={{ mb: 1.5, py: 0.25 }}>
                Connecting…
              </Alert>
            )}
            {deviceError && (
              <Alert severity="warning" sx={{ mb: 1.5, py: 0.25 }} onClose={clearError}>
                {deviceError.message}
              </Alert>
            )}

            {hasCall ? (
              <>
                <MDBox mb={1.5}>
                  {/* caller_name is resolved server-side on the inbound
                      webhook and carried on the ring event, so this is the
                      real name from the first frame. It covers leads too,
                      which contact_name/account_name alone did not. */}
                  <MDTypography variant="button" fontWeight="bold" color="dark" display="block">
                    {activeCall?.caller_name ||
                      activeCall?.contact_name ||
                      activeCall?.account_name ||
                      "Unknown caller"}
                  </MDTypography>
                  {counterparty && <MonoText variant="caption">{counterparty}</MonoText>}
                  {ourLine && (
                    <MDTypography
                      variant="caption"
                      display="block"
                      sx={{ color: MUTED_TEXT, mt: 0.25 }}
                    >
                      {activeCall?.direction === "outbound" ? "Calling from " : "Line "}
                      <MonoText variant="caption">{ourLine}</MonoText>
                    </MDTypography>
                  )}
                </MDBox>

                {isRinging ? (
                  <MDBox display="flex" justifyContent="space-around">
                    <RoundButton
                      icon={<Call />}
                      label="Accept"
                      onClick={acceptCall}
                      active
                    />
                    <RoundButton
                      icon={<CallEnd />}
                      label="Decline"
                      onClick={rejectCall}
                      danger
                    />
                  </MDBox>
                ) : isWrapUp ? (
                  /* The call is over. Mute, Keypad and Hang up were still
                     rendered here -- disabled, but present -- and the dock had
                     no way to leave wrap-up at all, so away from the Call
                     Console the widget sat on three dead buttons until the
                     server's own timer eventually freed the agent. Offer the
                     two things that are actually left to do instead. */
                  <MDBox display="flex" gap={1}>
                    <MDButton
                      fullWidth
                      variant="outlined"
                      color="info"
                      size="small"
                      // In-app navigation, not a page load: a reload threw
                      // away the wrap-up state, so the console opened on the
                      // dialler with nothing to save.
                      onClick={() =>
                        navigate(
                          activeCall?.id
                            ? `/call-console?call=${activeCall.id}`
                            : "/call-console"
                        )
                      }
                    >
                      Add outcome
                    </MDButton>
                    <MDButton
                      fullWidth
                      variant="gradient"
                      color="info"
                      size="small"
                      onClick={skipWrapUp}
                    >
                      Done
                    </MDButton>
                  </MDBox>
                ) : (
                  <>
                    <MDBox display="flex" justifyContent="space-around">
                      <RoundButton
                        icon={muted ? <MicOff /> : <Mic />}
                        label={muted ? "Unmute" : "Mute"}
                        onClick={toggleMute}
                        active={muted}
                        disabled={!isLive}
                      />
                      <RoundButton
                        icon={<Dialpad />}
                        label="Keypad"
                        onClick={() => setKeypadOpen((k) => !k)}
                        active={keypadOpen}
                        disabled={!isLive}
                      />
                      <RoundButton
                        icon={<CallEnd />}
                        label="Hang up"
                        onClick={hangUp}
                        danger
                        disabled={!isLive}
                      />
                    </MDBox>

                    {/* Inline, never a modal: the agent needs the record
                        visible while punching through an IVR. */}
                    <Collapse in={keypadOpen}>
                      <MDBox
                        mt={2}
                        sx={{
                          display: "grid",
                          gridTemplateColumns: "repeat(3, 1fr)",
                          gap: 0.75,
                        }}
                      >
                        {DTMF_KEYS.map((key) => (
                          <IconButton
                            key={key}
                            onClick={() => sendDigits(key)}
                            sx={{
                              borderRadius: "8px",
                              bgcolor: SURFACE,
                              color: DARK,
                              fontWeight: 600,
                              "&:hover": { bgcolor: "#E8EDF5" },
                            }}
                          >
                            <MonoText variant="button">{key}</MonoText>
                          </IconButton>
                        ))}
                      </MDBox>
                    </Collapse>
                  </>
                )}
              </>
            ) : (
              <>
                <MDBox
                  display="flex"
                  alignItems="center"
                  justifyContent="space-between"
                  sx={{ cursor: "pointer" }}
                  onClick={(e) => setPresenceAnchor(e.currentTarget)}
                >
                  <MDBox display="flex" alignItems="center" gap={1}>
                    <PresenceDot color={presenceMeta.color} />
                    <MDTypography variant="button" color="dark">
                      {presenceMeta.label}
                    </MDTypography>
                  </MDBox>
                  <ExpandLess sx={{ fontSize: 18, color: MUTED_TEXT }} />
                </MDBox>

                {/* Outbound dialling.
                    The keypad above this branch sends DTMF into a call that
                    already exists. Until now that was the only pad in the
                    dock, so an agent with a number on a sticky note and no
                    matching record had nowhere to type it -- the only way to
                    originate a call was to find a record with a call button on
                    it. */}
                <Divider sx={{ my: 1.5 }} />

                <MDBox display="flex" alignItems="center" justifyContent="space-between">
                  <MDTypography variant="caption" color="text" fontWeight="medium">
                    Dial a number
                  </MDTypography>
                  <IconButton
                    size="small"
                    onClick={() => setDialerOpen((d) => !d)}
                    aria-label={dialerOpen ? "Hide dialler" : "Show dialler"}
                    aria-expanded={dialerOpen}
                    sx={{ color: dialerOpen ? "#1565C8" : MUTED_TEXT }}
                  >
                    <Dialpad sx={{ fontSize: 18 }} />
                  </IconButton>
                </MDBox>

                <Collapse in={dialerOpen}>
                  <MDBox mt={1}>
                    {callerIds.length > 1 ? (
                      <TextField
                        select
                        fullWidth
                        size="small"
                        label="Calling from"
                        value={fromNumber?.id ?? ""}
                        onChange={(e) => setFromNumberId(Number(e.target.value))}
                        sx={{ mb: 1 }}
                      >
                        {callerIds.map((n) => (
                          <MenuItem key={n.id} value={n.id}>
                            {callerIdLabel(n)}
                          </MenuItem>
                        ))}
                      </TextField>
                    ) : fromNumber ? (
                      <MDTypography
                        variant="caption"
                        display="block"
                        sx={{ color: MUTED_TEXT, mb: 1 }}
                      >
                        Calling from <MonoText variant="caption">{fromNumber.e164}</MonoText>
                        {distinctFriendlyName(fromNumber) ? ` (${distinctFriendlyName(fromNumber)})` : ""}
                      </MDTypography>
                    ) : null}
                    <TextField
                      fullWidth
                      size="small"
                      value={dialNumber}
                      onChange={(e) => setDialNumber(sanitizeDialInput(e.target.value))}
                      // Enter dials, so a pasted number needs no reach for the
                      // mouse. Typing is kept as well as the pad: most numbers
                      // arrive by paste, not by twelve clicks.
                      onKeyDown={(e) => {
                        if (e.key === "Enter" && canDial) dial();
                      }}
                      placeholder="+61…"
                      inputProps={{ inputMode: "tel", "aria-label": "Number to dial" }}
                      InputProps={{
                        endAdornment: dialNumber ? (
                          <InputAdornment position="end">
                            <IconButton
                              size="small"
                              aria-label="Delete last digit"
                              onClick={() => setDialNumber((n) => n.slice(0, -1))}
                            >
                              <Backspace sx={{ fontSize: 16 }} />
                            </IconButton>
                          </InputAdornment>
                        ) : null,
                      }}
                    />

                    <MDBox mt={1}>
                      <DialpadKeys
                        size="small"
                        onKey={(key) => setDialNumber((n) => `${n}${key}`)}
                        disabled={dialBusy}
                      />
                    </MDBox>

                    {dialHint ? (
                      <MDTypography
                        variant="caption"
                        color="text"
                        display="block"
                        sx={{ mt: 1 }}
                      >
                        {dialHint}
                      </MDTypography>
                    ) : null}

                    <MDButton
                      fullWidth
                      variant="gradient"
                      color="success"
                      size="small"
                      sx={{ mt: 1 }}
                      disabled={!canDial}
                      onClick={dial}
                      startIcon={<Call />}
                    >
                      {dialBusy ? "Calling…" : "Call"}
                    </MDButton>
                  </MDBox>
                </Collapse>
              </>
            )}
          </MDBox>
        </Collapse>
      </Card>

      <Menu
        anchorEl={presenceAnchor}
        open={Boolean(presenceAnchor)}
        onClose={() => setPresenceAnchor(null)}
      >
        {AGENT_PRESENCE.map((p) => (
          <MenuItem
            key={p.value}
            selected={p.value === presence}
            onClick={async () => {
              setPresenceAnchor(null);
              await changePresence(p.value);
            }}
          >
            <MDBox display="flex" alignItems="center" gap={1}>
              <PresenceDot color={p.color} size={8} />
              <MDTypography variant="button">{p.label}</MDTypography>
            </MDBox>
          </MenuItem>
        ))}
      </Menu>

      <Snackbar
        open={Boolean(micError)}
        onClose={dismissMicError}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
      >
        <Alert severity="error" onClose={dismissMicError} sx={{ maxWidth: 420 }}>
          <MDTypography variant="button" fontWeight="bold" display="block">
            {micError?.message}
          </MDTypography>
          <MDTypography variant="caption" color="text">
            Click the lock icon in the address bar, allow the microphone, then set
            yourself Available again.
          </MDTypography>
        </Alert>
      </Snackbar>
    </>
  );
}
